import React, { useState, useEffect } from 'react';
import {
  Box,
  Tooltip,
  Menu,
  Avatar,
  MenuItem,
  Divider,
  ListItemIcon,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { deepOrange } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ApprovalIcon from '@mui/icons-material/Approval';
import Logout from '@mui/icons-material/Logout';
import MonitorIcon from '@mui/icons-material/Monitor';
import * as fileApi from '../../../lib/api/file';

const UserMenu = ({ user, onLogout }) => {
  let navigate = useNavigate();
  const theme = useTheme();
  // 모바일(md 미만) 여부 감지
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const linkToProfile = () => navigate('/profile');
  const linkToDashboard = () => navigate('/dashboard');
  const linkToApproval = () => navigate('/approvalmng');
  const linkToApprovalMonitoring = () => navigate('/approval_monitoring');

  const [profileImg, setProfileImg] = useState('');

  useEffect(() => {    
    const loadProfileImage = async () => {
      try {        
        if (user?.profileImg) {
          setProfileImg(await fileApi.profileImage(user.profileImg));
        }
      } catch (error) {
        console.error("이미지 호출 에러:", error);
      }
    };
    loadProfileImage();
    return () => {
      if (profileImg) URL.revokeObjectURL(profileImg);
    };
  }, [user?.profileImg]);

  // 💡 [권한 제어 변수 분리] 특정 부서가 아니거나, 혹은 지정된 예외 유저(usrUuid)일 때 true가 됩니다.
  const isApprovalVisible = user?.dptInf?.parentDptUuid !== 'D6D83465-EDF3-450D-ADEB-906BDD9FD086' || user?.usrUuid === '9A64EA44-3348-4144-B796-6C6537581E65';

  return (
    <Box>
      <Tooltip title="사용자 메뉴">
        <Button
          color="inherit"
          variant="text"
          onClick={handleClick}
          sx={{ 
            borderRadius: '10px', 
            // 모바일일 때 좌우 패딩 대폭 축소
            px: isMobile ? 0.5 : 1.5, 
            minWidth: isMobile ? 'auto' : '64px',
            textTransform: 'none'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* 사용자 정보: 모바일에서는 가독성을 위해 폰트 크기를 줄임 */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-end',
                mr: isMobile ? 0.5 : 1.5,
              }}
            >
              <Typography 
                variant={isMobile ? "caption" : "subtitle2"} 
                sx={{ 
                  fontWeight: 600, 
                  lineHeight: 1.2,
                  fontSize: isMobile ? '0.7rem' : 'inherit' // 모바일 초소형 폰트
                }}
              >
                {user?.usrNm}{'\u00a0'}
                {user?.gradeNm}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {user?.dptNm}
              </Typography>
            </Box>

            {/* 아바타: 모바일 상단바 높이에 맞춰 크기 축소 */}
            <Box>
              {user?.profileImg ? (
                <Avatar
                  alt={user?.usrNm}
                  src={profileImg}
                  sx={{ 
                    width: isMobile ? 26 : 32, 
                    height: isMobile ? 26 : 32,
                    border: `1px solid ${theme.palette.divider}`
                  }}
                />
              ) : (
                <Avatar
                  sx={{ 
                    bgcolor: deepOrange[500], 
                    width: isMobile ? 26 : 32, 
                    height: isMobile ? 26 : 32,
                    fontSize: isMobile ? '0.75rem' : '0.9rem'
                  }}
                >
                  {user?.usrNm?.substring(0, 1)}
                </Avatar>
              )}
            </Box>
          </Box>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            mt: 1,
            // 모바일에서 메뉴 아이템 간격 및 폰트 조절
            '& .MuiMenuItem-root': {
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              py: isMobile ? 1 : 1.2,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={linkToDashboard}>
          <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
          DASHBOARD
        </MenuItem>
        <MenuItem onClick={linkToProfile}>
          <ListItemIcon><ManageAccountsIcon fontSize="small" /></ListItemIcon>
          PROFILE
        </MenuItem>
        <Divider />
        
        {/* 💡 MUI Menu 가이드에 맞춰 Fragment(<></>)를 제거하고 각각 인라인 조건으로 교정했습니다. */}
        {isApprovalVisible && (
          <MenuItem onClick={linkToApproval}>
            <ListItemIcon><ApprovalIcon fontSize="small" /></ListItemIcon>
            결재관리
          </MenuItem>
        )}
        {isApprovalVisible && (
          <MenuItem onClick={linkToApprovalMonitoring}>
            <ListItemIcon><MonitorIcon fontSize="small" /></ListItemIcon>
            결재 모니터링
          </MenuItem>
        )}
        {isApprovalVisible && <Divider />}

        <MenuItem onClick={onLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
          LOGOUT
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserMenu;
